import { useState } from "react";
import { useAuditLogs, useAuditLogsCount, useAuditLogTables } from "@/hooks/use-audit-logs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Select } from "@/components/ui/select";
import { Toolbar } from "@/components/ui/toolbar";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";

const PAGE_SIZE = 50;

function formatTime(ts: string): string {
  return new Date(ts).toLocaleString();
}

export default function AuditLogsPage() {
  const [page, setPage] = useState(0);
  const [tableFilter, setTableFilter] = useState("");
  const [opFilter, setOpFilter] = useState("");
  const skip = page * PAGE_SIZE;

  const params = {
    skip,
    limit: PAGE_SIZE,
    ...(tableFilter ? { table_name: tableFilter } : {}),
    ...(opFilter ? { operation: opFilter } : {}),
  };

  const { data: logs, isLoading, isFetching, refetch } = useAuditLogs(params);
  const { data: countData } = useAuditLogsCount(
    tableFilter || opFilter ? { table_name: tableFilter || undefined, operation: opFilter || undefined } : undefined
  );
  const { data: tables } = useAuditLogTables();
  const total = countData?.count ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const logList = Array.isArray(logs) ? logs : [];

  const opVariants: Record<string, "success" | "info" | "destructive"> = {
    CREATE: "success",
    UPDATE: "info",
    DELETE: "destructive",
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit Logs"
        description="Every configuration change recorded with who, what, and when. Retained for 7 days."
      />

      <Toolbar
        filters={
          <>
            <Select
              value={tableFilter}
              onChange={(e) => { setTableFilter(e.target.value); setPage(0); }}
              className="h-8 w-44 text-xs"
            >
              <option value="">All Tables</option>
              {tables?.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Select
              value={opFilter}
              onChange={(e) => { setOpFilter(e.target.value); setPage(0); }}
              className="h-8 w-32 text-xs"
            >
              <option value="">All Ops</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
            </Select>
          </>
        }
        count={`${total} entries`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
      />

      {isLoading ? (
        <div className="space-y-2 rounded-sm border border-border bg-card p-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : logList.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={24} />}
          title="No audit log entries"
          description="Configuration changes appear here as they happen. Adjust the filters to widen the search."
          className="rounded-sm border border-border bg-card"
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Record</TableHead>
              <TableHead>Operation</TableHead>
              <TableHead>Changed Fields</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Endpoint</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logList.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="whitespace-nowrap text-xs">
                  {formatTime(log.timestamp)}
                </TableCell>
                <TableCell className="text-xs">{log.table_name}</TableCell>
                <TableCell className="max-w-[150px] truncate font-mono text-xs">
                  {log.record_name || log.record_id}
                </TableCell>
                <TableCell>
                  <Badge variant={opVariants[log.operation] || "neutral"}>
                    {log.operation}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-xs">
                  {log.changed_fields?.join(", ") || "-"}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {log.ip_address || "-"}
                </TableCell>
                <TableCell className="max-w-[150px] truncate font-mono text-xs">
                  {log.endpoint || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground tabular-nums">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
